import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { playSound } from '@/lib/sounds';

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  kicker?: string;
  title?: string;
  description?: string;
  /** Ícono opcional al lado del título. Se achica con el header. */
  icon?: ReactNode;
  /**
   * Si está en true, el header se contrae al scrollear el contenido
   * (patrón "collapsing header" / "shrink on scroll"). Default false.
   */
  compactOnScroll?: boolean;
  /** Ancho en px. Default 520. */
  width?: number;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * Panel lateral (slide-in desde la derecha). Reemplaza al Modal en flujos
 * de edición largos — la ficha vive en el lateral, el contexto atrás queda
 * visible al 40%. Patrón del manual de marca: "profundidad como segunda capa".
 *
 * `compactOnScroll`: el header se contrae cuando el usuario scrollea más
 * de 60px en el contenido — typografía más chica, ícono reducido,
 * descripción/kicker ocultos. Patrón típico de apps con headers grandes
 * que ahorran espacio sin perder presencia visual.
 */
export function Drawer({
  open,
  onClose,
  kicker,
  title,
  description,
  icon,
  compactOnScroll = false,
  width = 520,
  children,
  footer,
}: DrawerProps) {
  const wasOpenRef = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [compact, setCompact] = useState(false);

  useEffect(() => {
    if (open && !wasOpenRef.current) playSound('drawer_open');
    else if (!open && wasOpenRef.current) playSound('drawer_close');
    wasOpenRef.current = open;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  /**
   * Collapsing header con histéresis: dos thresholds (40 / 90) para evitar
   * el flicker cuando el scroll oscila justo en el límite. Usa rAF para
   * leer scrollTop sin bloquear el frame y dejar que la transición CSS
   * (cubic-bezier suave) haga el trabajo de animación.
   */
  useEffect(() => {
    if (!open || !compactOnScroll) return;
    const el = contentRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const top = el.scrollTop;
        setCompact((prev) => {
          if (prev && top < 40) return false;
          if (!prev && top > 90) return true;
          return prev;
        });
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [open, compactOnScroll]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end" aria-modal role="dialog">
      <div
        className="fixed inset-0 animate-[fadeIn_.18s_ease-out] bg-neutral-ink/40 backdrop-blur-[2px]"
        style={{
          animation: 'fadeIn 180ms ease-out',
        }}
        onClick={onClose}
        aria-hidden
      />
      <div
        className={cn(
          'relative z-10 flex h-full flex-col overflow-hidden bg-white shadow-premium',
          'animate-in slide-in-from-right',
        )}
        style={{
          width: '100%',
          maxWidth: width,
          animation: 'slideInRight 240ms cubic-bezier(.2,.8,.2,1)',
        }}
      >
        {(title || kicker) && (
          <div
            className={cn(
              'drawer-header flex items-start justify-between gap-4 border-b border-surface-line',
              compact && 'is-compact',
            )}
          >
            <div className="flex min-w-0 items-start gap-3">
              {icon && (
                <div className="drawer-icon grid flex-none place-items-center rounded-md bg-blue-10 text-blue">
                  {icon}
                </div>
              )}
              <div className="min-w-0 flex-1">
                {kicker && (
                  <div className="drawer-kicker">
                    <p className="kicker">{kicker}</p>
                  </div>
                )}
                {title && (
                  <h2 className="drawer-title truncate font-extrabold tracking-tight text-neutral-ink">
                    {title}
                  </h2>
                )}
                {description && (
                  <div className="drawer-desc">
                    <p className="text-sm text-neutral-muted">{description}</p>
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="grid h-8 w-8 flex-none place-items-center rounded-md text-neutral-muted transition hover:bg-blue-10 hover:text-blue"
              aria-label="Cerrar"
            >
              <X size={16} />
            </button>
          </div>
        )}

        <div ref={contentRef} className="flex-1 overflow-y-auto px-6 py-5">
          {children}
        </div>

        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-surface-line bg-surface-bg/40 px-6 py-4">
            {footer}
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideInRight {
          from { transform: translateX(24px); opacity: 0 }
          to { transform: translateX(0); opacity: 1 }
        }

        /* Collapsing header con curva suave (cubic-bezier estilo material).
           Se anima padding, font-size, height, opacity y altura colapsada
           usando grid-template-rows para que kicker/desc desaparezcan en
           transición fluida en vez de salto. */
        .drawer-header {
          padding: 1.25rem 1.5rem;
          transition: padding 280ms cubic-bezier(.32,.72,0,1);
          will-change: padding;
        }
        .drawer-header.is-compact {
          padding: 0.5rem 1.5rem 0.55rem;
        }
        .drawer-icon {
          height: 2.5rem; width: 2.5rem;
          transition:
            height 280ms cubic-bezier(.32,.72,0,1),
            width 280ms cubic-bezier(.32,.72,0,1),
            transform 280ms cubic-bezier(.32,.72,0,1);
          will-change: height, width, transform;
        }
        .drawer-header.is-compact .drawer-icon {
          height: 1.75rem; width: 1.75rem;
          transform: scale(.95);
        }
        .drawer-icon > * {
          transition: transform 280ms cubic-bezier(.32,.72,0,1);
        }
        .drawer-header.is-compact .drawer-icon > * { transform: scale(.78); }

        .drawer-title {
          font-size: 1.25rem;
          line-height: 1.75rem;
          transition:
            font-size 280ms cubic-bezier(.32,.72,0,1),
            line-height 280ms cubic-bezier(.32,.72,0,1);
          will-change: font-size, line-height;
        }
        .drawer-header.is-compact .drawer-title {
          font-size: 1rem;
          line-height: 1.4rem;
        }

        /* Kicker y description colapsan altura + opacidad juntos para una
           transición orgánica sin "saltos". grid-template-rows + overflow
           es el trick clásico para animar height auto. */
        .drawer-kicker, .drawer-desc {
          display: grid;
          grid-template-rows: 1fr;
          opacity: 1;
          transition:
            grid-template-rows 320ms cubic-bezier(.32,.72,0,1),
            opacity 200ms ease-out,
            margin 280ms cubic-bezier(.32,.72,0,1);
          will-change: grid-template-rows, opacity;
        }
        .drawer-kicker { margin-bottom: 0.375rem; }
        .drawer-desc { margin-top: 0.25rem; }
        .drawer-header.is-compact .drawer-kicker,
        .drawer-header.is-compact .drawer-desc {
          grid-template-rows: 0fr;
          opacity: 0;
          margin: 0;
        }
        .drawer-kicker > *, .drawer-desc > * { min-height: 0; overflow: hidden; }
      `}</style>
    </div>,
    document.body,
  );
}
